<template>
    <v-container>
        <v-row justify="center">
            <v-col cols="12" sm="12" md="12">
                <v-card class="pa-4" elevation="4">
                    <v-card-title>
                        <h2 class="text-h5 font-weight-bold text-grey-darken-4">
                            {{ isEditing ? 'Editar Plano de Assinatura' : 'Novo Plano de Assinatura' }}
                        </h2>
                    </v-card-title>

                    <v-card-text>
                        <v-form ref="formRef" @submit.prevent="handleSubmit">
                            <v-row class="mt-2">
                                <v-col cols="12" sm="3" v-if="isEditing">
                                    <v-text-field v-model="formValue.code" label="Código" disabled />
                                </v-col>

                                <v-col :cols="isEditing ? 12 : 12" :sm="isEditing ? 9 : 6">
                                    <v-text-field v-model="formValue.name" label="Nome do Plano"
                                        :rules="[rules.name]" />
                                </v-col>

                                <v-col v-if="!isEditing" :cols="isEditing ? 12 : 12" :sm="isEditing ? 9 : 6">
                                    <v-text-field v-model="formValue.code" label="Código" placeholder="Exemplo: TESTE"
                                        :rules="[rules.code]" />
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-select v-model="formValue.plan_type" :items="planTypeOptions" item-title="label"
                                        item-value="value" label="Tipo de Recorrência" :rules="[rules.plan_type]" />
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="amountFormatted" label="Valor" prefix="R$"
                                        :rules="[rules.amount]" @input="formatAmount" />
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="amountFeaturedFormatted" label="Adicional por Destaque"
                                        prefix="R$" @input="formatAmountFeatured"
                                        hint="Valor extra para ativar 'Em Destaque'." persistent-hint />
                                </v-col>

                                <v-col cols="12" sm="3">
                                    <v-text-field v-model="amountImpulseFormatted" label="Adicional por Impulso"
                                        prefix="R$" @input="formatAmountImpulse"
                                        hint="Valor extra para ativar impsulo." persistent-hint />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-switch v-model="formValue.is_active" color="success" label="Plano Ativo?"
                                        :true-value="1" :false-value="0" hint="Define se o plano pode ser contratado."
                                        persistent-hint />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-switch v-model="formValue.is_employability_plan" color="primary"
                                        label="Plano de Empregabilidade?" :true-value="1" :false-value="0"
                                        hint="Define se o plano é voltado para empregabilidade." persistent-hint />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>

                    <v-card-actions class="d-flex justify-space-between pt-4">
                        <v-btn color="secondary" variant="flat" @click="handleCancel">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                            @click="handleSubmit">
                            <v-icon left>mdi-content-save</v-icon>
                            {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Plano' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore'

const toastStore = useToastStore()
const loadingStore = useLoadingStore()
const route = useRoute()
const router = useRouter()
const formRef = ref(null)

const isLoading = ref(false)
const isSubmitting = ref(false)

const isEditing = computed(() => !!route.params.id)

const formValue = ref({
    code: '',
    name: '',
    plan_type: 'monthly',
    amount: null,
    currency: 'BRL',
    is_active: 1,
    amount_featured: null,
    amount_impulse: null,
    is_employability_plan: 0,
})

// Campos formatados para exibição
const amountFormatted = ref('')
const amountFeaturedFormatted = ref('')
const amountImpulseFormatted = ref('')

// Opções de Tipo de Plano (baseado no ENUM da DDL)
const planTypeOptions = [
    { label: 'Mensal', value: 'monthly' },
    { label: 'Semestral', value: 'semi-annual' },
    { label: 'Anual', value: 'annual' },
]

// Regras de Validação
const rules = {
    name: (v) => !!v || 'O nome do plano é obrigatório.',
    code: (v) => {
        if (!v) {
            return 'O código do plano é obrigatório.'
        }
        // Testa se a string CONTÉM um ou mais espaços em branco (\s+)
        if (/\s+/.test(v)) {
            return 'O código não pode conter espaços.'
        }
        return true 
    },
    plan_type: (v) => !!v || 'O tipo de recorrência é obrigatório.',
    amount: () =>
        (formValue.value.amount !== null &&
            formValue.value.amount !== undefined &&
            formValue.value.amount >= 0) || // MUDANÇA AQUI: de > 0 para >= 0
        'O valor base é obrigatório e deve ser maior ou igual a zero.', // Mensagem atualizada
    currency: (v) => !!v || 'A moeda é obrigatória.',
}

const formatNumberValue = (value) => {
    if (value === null || value === undefined || isNaN(value)) return ''
    return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    })
}

const parseCurrencyToFloat = (raw) => {
    const clean = String(raw).replace(/[^\d]/g, '')
    const num = clean ? parseInt(clean, 10) / 100 : 0
    return isNaN(num) ? null : num
}

const formatAmount = (event) => {
    const raw = event.target ? event.target.value : event
    formValue.value.amount = parseCurrencyToFloat(raw)
    amountFormatted.value = formatNumberValue(formValue.value.amount)
}

const formatAmountFeatured = (event) => {
    const raw = event.target ? event.target.value : event
    formValue.value.amount_featured = parseCurrencyToFloat(raw)
    amountFeaturedFormatted.value = formatNumberValue(
        formValue.value.amount_featured
    )
}

const formatAmountImpulse = (event) => {
    const raw = event.target ? event.target.value : event
    formValue.value.amount_impulse = parseCurrencyToFloat(raw)
    amountImpulseFormatted.value = formatNumberValue(
        formValue.value.amount_impulse
    )
}

const loadSubscription = async (id) => {
    isLoading.value = true
    loadingStore.show()
    try {
        const response = await api.get(`/subscriptions/${id}`)
        const data = response.data.data

        if (!data) {
            toastStore.showToast({
                message: 'O plano não foi encontrado.',
                color: 'warning',
            })
            return
        }

        formValue.value = {
            ...formValue.value,
            ...data,
            amount: data.amount ? parseFloat(data.amount) : null,
            amount_featured: data.amount_featured
                ? parseFloat(data.amount_featured)
                : null,
            amount_impulse: data.amount_impulse
                ? parseFloat(data.amount_impulse)
                : null,
            is_active: data.is_active === 1 ? 1 : 0,
            is_employability_plan: data.is_employability_plan === 1 ? 1 : 0,
            currency: data.currency || 'BRL',
        }

        // Inicializa campos formatados usando a nova função (SEM R$)
        amountFormatted.value = formatNumberValue(formValue.value.amount)
        amountFeaturedFormatted.value = formatNumberValue(
            formValue.value.amount_featured
        )
        amountImpulseFormatted.value = formatNumberValue(
            formValue.value.amount_impulse
        )

        await nextTick()
        if (formRef.value) formRef.value.resetValidation()
    } catch (error) {
        console.error('Erro ao carregar os dados do plano:', error)
        toastStore.showToast({
            message:
                error?.response?.data?.error ||
                'Erro ao carregar os dados do plano de assinatura.',
            color: 'error',
        })
    } finally {
        isLoading.value = false
        loadingStore.hide()
    }
}

const handleSubmit = async () => {
    const { valid } = await formRef.value.validate()
    if (!valid) return

    isSubmitting.value = true

    // Cria o payload de dados
    const payload = {
        ...formValue.value,
        amount: formValue.value.amount,
        amount_featured: formValue.value.amount_featured,
        amount_impulse: formValue.value.amount_impulse,
        is_active: formValue.value.is_active,
        is_employability_plan: formValue.value.is_employability_plan,
    }

    try {
        if (isEditing.value) {
            await api.put(`/subscriptions/${route.params.id}`, payload)
        } else {
            await api.post('/subscriptions', payload)
        }

        router.push('/app/assinaturas')
        toastStore.showToast({
            message: isEditing.value
                ? 'Plano atualizado com sucesso!'
                : 'Plano cadastrado com sucesso!',
            color: 'success',
        })
    } catch (error) {
        console.error('Erro ao salvar plano:', error)
        const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Erro ao salvar o plano de assinatura.'

        toastStore.showToast({ message: errorMessage, color: 'error' })
    } finally {
        isSubmitting.value = false
    }
}

const handleCancel = () => {
    router.push('/app/assinaturas')
}

onMounted(() => {
    if (isEditing.value) {
        loadSubscription(route.params.id)
    }
})
</script>